/**
 * Network node management for activity particles
 */

import type * as THREE_NS from 'three';

export interface NetworkNode {
  position: { x: number; y: number; z: number };
  id: number;
}

/**
 * Extract network nodes from grid particles
 */
export function extractNodesFromParticles(
  particles: THREE_NS.Points
): NetworkNode[] {
  const networkNodes: NetworkNode[] = [];

  if (particles && particles.geometry) {
    const positions = particles.geometry.attributes.position.array as Float32Array;
    const positionCount = positions.length / 3;

    // Store all grid points as nodes - these are the actual network nodes
    for (let i = 0; i < positionCount; i++) {
      const x = positions[i * 3];
      const y = positions[i * 3 + 1];
      const z = positions[i * 3 + 2];
      networkNodes.push({ position: { x, y, z }, id: i });
    }

    // Create a spatial lookup map for finding nearest grid points
    const gridSpacing = 8; // Grid spacing from particle creation
    const gridLookup = new Map<string, NetworkNode>();
    networkNodes.forEach((node) => {
      const key = `${Math.round(node.position.x / gridSpacing)},${Math.round(node.position.y / gridSpacing)},${Math.round(node.position.z / gridSpacing)}`;
      gridLookup.set(key, node);
    });

    // Store grid spacing and lookup for path generation
    (window as any).__networkGridSpacing = gridSpacing;
    (window as any).__networkGridLookup = gridLookup;
  }

  // Add distant horizontal nodes (distant servers on ground plane)
  const groundY = -60;
  const distantHorizontalNodes = [
    { x: -180, y: groundY, z: 0 },
    { x: 180, y: groundY, z: 0 },
    { x: 0, y: groundY, z: -180 },
    { x: 0, y: groundY, z: 180 },
    { x: -150, y: groundY, z: -150 },
    { x: 150, y: groundY, z: 150 },
    { x: -150, y: groundY, z: 150 },
    { x: 150, y: groundY, z: -150 },
  ];
  distantHorizontalNodes.forEach((pos, i) => {
    networkNodes.push({ position: pos, id: networkNodes.length + i });
  });

  // Add high-altitude nodes (from sky)
  const skyY = 120;
  const skyNodes = [
    { x: 0, y: skyY, z: 0 },
    { x: -50, y: skyY, z: -50 },
    { x: 50, y: skyY, z: 50 },
    { x: -50, y: skyY, z: 50 },
    { x: 50, y: skyY, z: -50 },
  ];
  skyNodes.forEach((pos, i) => {
    networkNodes.push({ position: pos, id: networkNodes.length + i });
  });

  return networkNodes;
}
